import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
const SLIDER_MIN = 100000;
const SLIDER_MAX = 2000000;
const SLIDER_STEP = 10000;
const presets = [{
  label: '$500K',
  value: 500000
}, {
  label: '$750K',
  value: 750000
}, {
  label: '$1M',
  value: 1000000
}, {
  label: '$1.5M',
  value: 1500000
}];
export function SavingsCalculator() {
  const [homeValue, setHomeValue] = useState(500000);
  const sliderRef = useRef<HTMLInputElement>(null);
  const roundedValue = Math.round(homeValue / 10000) * 10000;
  const traditionalFee = roundedValue * 0.03;
  const onePercentFee = roundedValue * 0.01;
  const savings = roundedValue * 0.02;
  const fillPercent = (homeValue - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN) * 100;
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  const handleSliderInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setHomeValue(Number((e.target as HTMLInputElement).value));
  }, []);
  const handlePresetClick = useCallback((value: number) => {
    setHomeValue(value);
  }, []);
  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.style.setProperty('--fill', `${fillPercent}%`);
    }
  }, [fillPercent]);
  return <section className="py-20 md:py-32 bg-charcoal text-white" data-id="element-247">
      <style data-id="element-248">{`
        .calc-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 8px;
          background: transparent;
          cursor: pointer;
          touch-action: pan-y;
          position: relative;
          z-index: 2;
        }
        .calc-slider:focus {
          outline: none;
        }
        .calc-slider::-webkit-slider-runnable-track {
          height: 8px;
          background: linear-gradient(
            to right,
            #C0392B 0%,
            #C0392B var(--fill, 50%),
            rgba(255,255,255,0.2) var(--fill, 50%)
          );
          border-radius: 4px;
        }
        .calc-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          height: 28px;
          width: 28px;
          border-radius: 50%;
          background: #E5C77A;
          border: 2px solid #FFFFFF;
          margin-top: -10px;
          cursor: grab;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          transition: box-shadow 0.15s ease;
        }
        .calc-slider::-webkit-slider-thumb:active {
          cursor: grabbing;
          box-shadow: 0 0 0 8px rgba(192, 57, 43, 0.15), 0 2px 8px rgba(0,0,0,0.3);
        }
        .calc-slider::-moz-range-track {
          height: 8px;
          background: linear-gradient(
            to right,
            #C0392B 0%,
            #C0392B var(--fill, 50%),
            rgba(255,255,255,0.2) var(--fill, 50%)
          );
          border-radius: 4px;
          border: none;
        }
        .calc-slider::-moz-range-thumb {
          height: 28px;
          width: 28px;
          border-radius: 50%;
          background: #E5C77A;
          border: 2px solid #FFFFFF;
          cursor: grab;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .calc-slider::-moz-range-thumb:active {
          cursor: grabbing;
          box-shadow: 0 0 0 8px rgba(192, 57, 43, 0.15), 0 2px 8px rgba(0,0,0,0.3);
        }
        @media (max-width: 768px) {
          .calc-slider::-webkit-slider-thumb {
            height: 36px;
            width: 36px;
            margin-top: -14px;
          }
          .calc-slider::-moz-range-thumb {
            height: 36px;
            width: 36px;
          }
        }
      `}</style>

      <div className="container mx-auto px-4 md:px-6" data-id="element-249">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-250">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-4" data-id="element-251">
            See how much more you keep.
          </h2>
          <p className="text-white/70 text-lg" data-id="element-252">
            Slide to your home value. Watch your savings grow.
          </p>
        </div>

        <div className="max-w-3xl mx-auto bg-white/5 border border-white/10 rounded-xl p-8 md:p-12 backdrop-blur-sm" data-id="element-253">
          {/* Home Value Display */}
          <div className="text-center mb-10" data-id="element-254">
            <span className="text-sm text-gold uppercase tracking-wider font-medium mb-2 block" data-id="element-255">
              Estimated Home Value
            </span>
            <div className="font-serif text-5xl md:text-6xl text-white" data-id="element-256">
              {formatCurrency(roundedValue)}
            </div>
          </div>

          {/* Native Range Slider */}
          <div className="mb-12 px-2" data-id="element-257">
            <div className="py-6 cursor-pointer relative z-10" data-id="element-258">
              <input ref={sliderRef} type="range" min={SLIDER_MIN} max={SLIDER_MAX} step={SLIDER_STEP} value={homeValue} onInput={handleSliderInput} aria-label="Home value" aria-valuemin={SLIDER_MIN} aria-valuemax={SLIDER_MAX} aria-valuenow={roundedValue} aria-valuetext={formatCurrency(roundedValue)} className="calc-slider" style={{
              '--fill': `${fillPercent}%`
            } as React.CSSProperties} data-id="element-259" />
            </div>
            <div className="flex justify-between text-xs text-white/50 font-medium" data-id="element-260">
              <span data-id="element-261">$100K</span>
              <span data-id="element-262">$2M</span>
            </div>

            {/* Quick-select presets */}
            <div className="flex justify-center gap-3 mt-6" data-id="element-263">
              {presets.map(preset => <button key={preset.value} onClick={() => handlePresetClick(preset.value)} className={`px-4 py-2 text-sm rounded-full border transition-all duration-200 ${roundedValue === preset.value ? 'bg-brick-red border-brick-red text-white' : 'border-gold/40 text-white/80 hover:bg-brick-red hover:border-brick-red hover:text-white'}`} data-id="element-264">
                  {preset.label}
                </button>)}
            </div>
          </div>

          {/* Fee Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10 pb-10 border-b border-white/10" data-id="element-265">
            <div className="text-center md:text-left" data-id="element-266">
              <span className="text-sm text-white/60 block mb-1" data-id="element-267">
                Traditional Listing
              </span>
              <span className="text-xs text-white/40 block mb-2" data-id="element-268">
                3% listing fee
              </span>
              <div className="text-3xl font-serif" style={{
              color: '#9CA3AF'
            }} data-id="element-269">
                {formatCurrency(traditionalFee)}
              </div>
            </div>
            <div className="text-center md:text-right" data-id="element-270">
              <span className="text-sm text-gold block mb-1" data-id="element-271">
                One Percent Listing AZ
              </span>
              <span className="text-xs text-white/40 block mb-2" data-id="element-272">
                1% listing fee
              </span>
              <div className="text-3xl font-serif text-white font-semibold" data-id="element-273">
                {formatCurrency(onePercentFee)}
              </div>
            </div>
          </div>

          {/* Savings Callout */}
          <div className="text-center" data-id="element-274">
            <div className="mb-8" data-id="element-275">
              <span className="text-lg text-white/80 block mb-3" data-id="element-276">You save</span>
              <div className="text-5xl md:text-7xl font-serif text-brick-red font-bold leading-none" data-id="element-277">
                {formatCurrency(savings)}
              </div>
            </div>
            <p className="text-xs text-white/40 mb-8 max-w-md mx-auto" data-id="element-278">
              Based on 3% traditional listing side commission. Buyer side
              commission negotiated separately.
            </p>
            <Button className="bg-brick-red hover:bg-brick-red-dark text-white h-14 px-10 text-lg w-full sm:w-auto" data-id="element-279">
              Book My Free Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>;
}